import { Text, VStack } from '@chakra-ui/react';
import {
  Button,
  CarouselProvider,
  Grid,
  type LinkButtonProps,
  MotionBox,
} from '@time-line/components/common';
import { AnimatePresence } from 'motion/react';
import NextLink from 'next/link';
import { useState } from 'react';
import { useMeasure } from 'react-use';
import type { SocialMediaCarousel as SocialMediaCarouselProps } from 'types/cms/blocks';
import { useDebounce } from 'use-debounce';

import { ANIMATION_DURATION } from './constants';
import { SocialMediaCarouselItems } from './SocialMediaCarouselItems';

function CTALink({
  href,
  children,
  ...props
}: LinkButtonProps & { href: string }) {
  const isExternal = typeof href === 'string' && href.startsWith('http');

  const buttonProps = {
    variant: 'outline',
    width: { base: '100%', sm: 'auto' },
    ...props,
  };

  if (isExternal) {
    return (
      <Button
        as="a"
        href={href}
        rel="noopener noreferrer"
        target="_blank"
        {...buttonProps}
      >
        {children}
      </Button>
    );
  }

  return (
    <Button as={NextLink} href={href} {...buttonProps}>
      {children}
    </Button>
  );
}

export function SocialMediaCarousel({
  eyebrow,
  items,
  cta,
}: SocialMediaCarouselProps) {
  const [selectedItemIdx, setSelectedItemIdx] = useState<number>(0);
  const selectedItem =
    items.length > 0 ? items[selectedItemIdx % items.length] : undefined;
  /* INFO: text-wrap: balance can re-flow the measured element after the
   * initial layout pass. Debouncing the measured height coalesces those
   * fires so we only animate to the settled value. */
  const [measureRef, { height: measuredHeight }] = useMeasure<HTMLDivElement>();
  const [debouncedHeight] = useDebounce(measuredHeight, 250);
  const contentHeight: number | 'auto' =
    debouncedHeight > 0 ? debouncedHeight : 'auto';

  return (
    <Grid
      className="section--social-media-carousel"
      maxW="100%"
      justifyContent="center"
      alignItems="center"
      rowGap={{ base: 'm', md: 'lg' }}
      my="2xl"
    >
      <CarouselProvider>
        <SocialMediaCarouselItems
          items={items}
          selectedItemIdx={selectedItemIdx}
          setSelectedItemIdx={setSelectedItemIdx}
        />
      </CarouselProvider>
      <Grid.Inner>
        <VStack
          width="100%"
          gap={0}
          direction={{ base: 'column', sm: 'row' }}
          alignItems="center"
          justifyContent="center"
          gridColumn={{ base: '1 / -1', md: '3 / span 8' }}
        >
          <Text as="h5" size="overline" pb="3xs">
            {eyebrow}
          </Text>
          <MotionBox
            animate={{ height: contentHeight }}
            transition={{ duration: ANIMATION_DURATION / 2, ease: 'easeInOut' }}
            overflow="hidden"
            position="relative"
          >
            {/* INFO: Framer-Motion's layout prop wasn't working so this is
             * a hidden element to measure it's height to animate. */}
            <VStack
              ref={measureRef}
              spacing={0}
              position="absolute"
              top={0}
              left={0}
              right={0}
              visibility="hidden"
              pointerEvents="none"
            >
              {selectedItem?.pullQuote && (
                <Text
                  size="large-serif"
                  as="q"
                  textAlign="center"
                  sx={{ textWrap: 'balance' }}
                  noOfLines={3}
                >
                  {selectedItem.pullQuote}
                </Text>
              )}
            </VStack>

            <VStack spacing={0}>
              <AnimatePresence mode="wait">
                <MotionBox
                  key={
                    items.length > 0
                      ? selectedItemIdx % items.length
                      : selectedItemIdx
                  }
                  initial={{ opacity: 0 }}
                  animate={{
                    opacity: 1,
                    transition: { delay: ANIMATION_DURATION / 4 },
                  }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: ANIMATION_DURATION / 2 }}
                >
                  {selectedItem?.pullQuote && (
                    <Text
                      size="large-serif"
                      as="q"
                      textAlign="center"
                      sx={{ textWrap: 'balance' }}
                      noOfLines={3}
                    >
                      {selectedItem.pullQuote}
                    </Text>
                  )}
                </MotionBox>
              </AnimatePresence>
            </VStack>
          </MotionBox>
          {cta && (
            <CTALink mt={{ base: 'sm', md: 'm' }} href={cta.href}>
              {cta.title}
            </CTALink>
          )}
        </VStack>
      </Grid.Inner>
    </Grid>
  );
}
